import React, { useReducer, useEffect, useRef, useState, useCallback } from 'react';
import type { PowerUpItem } from '@/types/game';
import { GameStatus, PowerUpType } from '@/types/game';
import { GAME_CONFIG, COLOR_SCHEME } from '@/game/constants';
import {
  gameReducer,
  createInitialState,
  generateRandomPowerUpPosition,
  randomPowerUpType,
} from '@/game/reducer';
import { getSnakeGradientColors } from '@/game/particles';
import {
  playEatSound,
  playPowerUpSound,
  playGameOverSound,
  playMoveSound,
  playStartSound,
} from '@/game/audio';
import { useKeyboardInput } from '@/hooks/useInput';
import GameCanvas from '@/components/GameCanvas';
import ScoreBoard from '@/components/ScoreBoard';
import ActiveEffects from '@/components/ActiveEffects';
import GameOverlay from '@/components/GameOverlay';

/**
 * 霓虹蛇境 (Neon Snake Realm) - 根组件
 *
 * 负责游戏状态管理、键盘输入处理、游戏主循环编排和整体布局。
 * 所有游戏逻辑模块（reducer、碰撞检测、粒子系统、渲染器）均通过 import 引入，
 * 组件本身仅负责协调和组合。
 */
const App: React.FC = () => {
  // ==========================================================================
  // 游戏状态管理
  // ==========================================================================

    const [gameState, dispatch] = useReducer(gameReducer, undefined, createInitialState);

  // ==========================================================================
  // 音效触发 - 监听游戏状态变化
  // ==========================================================================

  const prevStatusRef = useRef<GameStatus>(gameState.status);
  const prevScoreRef = useRef<number>(gameState.score.current);
  const prevEffectsCountRef = useRef<number>(gameState.activeEffects.length);

  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    const prevScore = prevScoreRef.current;
    const prevEffectsCount = prevEffectsCountRef.current;

    // 启动音效：从 Idle 或 GameOver 进入 Playing
    if (
      gameState.status === GameStatus.Playing &&
      prevStatus !== GameStatus.Playing
    ) {
      playStartSound();
    }

    // 游戏结束音效：进入 GameOver 状态
    if (
      gameState.status === GameStatus.GameOver &&
      prevStatus === GameStatus.Playing
    ) {
      playGameOverSound();
    }

    // 吃食物音效：分数增加
    if (gameState.score.current > prevScore && prevScore >= 0) {
      playEatSound();
    }

    // 收集道具音效：activeEffects 数量增加
    if (gameState.activeEffects.length > prevEffectsCount) {
      playPowerUpSound();
    }

    // 更新 refs
    prevStatusRef.current = gameState.status;
    prevScoreRef.current = gameState.score.current;
    prevEffectsCountRef.current = gameState.activeEffects.length;
  }, [gameState.status, gameState.score.current, gameState.activeEffects.length]);

  // ==========================================================================
  // 键盘输入处理
  // ==========================================================================

  useKeyboardInput(dispatch, gameState.status);

  // ==========================================================================
  // 响应式设备检测
  // ==========================================================================

  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== 'undefined'
      ? window.innerWidth < GAME_CONFIG.mobileBreakpoint
      : false
  );

  useEffect(() => {
    const handleResize = (): void => {
      setIsMobile(window.innerWidth < GAME_CONFIG.mobileBreakpoint);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ==========================================================================
  // 游戏循环计时器 Refs
  // ==========================================================================

  /** 道具生成计时器（累计毫秒） */
  const powerUpTimerRef = useRef<number>(0);
  /** 蛇移动计时器（累计毫秒） */
  const moveTimerRef = useRef<number>(0);
  /** 动画帧 ID */
  const animationFrameRef = useRef<number>(0);
  /** 上一帧时间戳（毫秒） */
  const lastTimeRef = useRef<number>(0);

  // ==========================================================================
  // 游戏主循环
  // ==========================================================================

  /**
   * 每帧调用的 tick 回调。
   *
   * 由 GameCanvas 的 requestAnimationFrame 循环调用，
   * 负责更新粒子、道具效果计时器、道具生成和蛇的移动。
   *
   * @param deltaTime - 自上一帧以来的时间增量（毫秒）
   */
  const handleTick = useCallback(
    (deltaTime: number): void => {
      // 更新粒子系统
      dispatch({ type: 'UPDATE_PARTICLES', deltaTime });

      // 更新道具效果计时器
      dispatch({ type: 'UPDATE_EFFECTS', deltaTime });

      // 道具生成计时器
      powerUpTimerRef.current += deltaTime;
      if (
        gameState.powerUpItem === null &&
        powerUpTimerRef.current >= GAME_CONFIG.powerUpSpawnInterval
      ) {
        powerUpTimerRef.current = 0;
                const powerUpPosition = generateRandomPowerUpPosition(
          gameState.snake.body,
          gameState.food.position,
          gameState.grid,
          gameState.obstacles
        );
        const powerUp: PowerUpItem = {
          position: powerUpPosition,
          type: randomPowerUpType(),
          spawnTime: Date.now(),
        };
        dispatch({ type: 'SPAWN_POWERUP', powerUp });
      }

      // 蛇移动计时器（考虑加速/减速效果）
      moveTimerRef.current += deltaTime;

      const hasSpeed = gameState.activeEffects.some(
        (e) => e.type === PowerUpType.Speed
      );
      const hasSlow = gameState.activeEffects.some(
        (e) => e.type === PowerUpType.Slow
      );

      let moveInterval = GAME_CONFIG.baseMoveInterval;
      if (hasSpeed) {
        moveInterval = Math.round(moveInterval / GAME_CONFIG.speedMultiplier);
      }
      if (hasSlow) {
        moveInterval = Math.round(moveInterval / GAME_CONFIG.slowMultiplier);
      }

            if (moveTimerRef.current >= moveInterval) {
        moveTimerRef.current -= moveInterval;
        dispatch({ type: 'TICK', deltaTime: moveInterval });
        // 播放移动音效
        playMoveSound();
      }
    },
    [gameState.powerUpItem, gameState.snake.body, gameState.food.position, gameState.grid, gameState.activeEffects]
  );

  // ==========================================================================
  // 游戏循环生命周期管理
  // ==========================================================================

  /**
   * 管理 requestAnimationFrame 游戏循环。
   *
   * 循环运行条件：
   * - 游戏状态为 Playing（正常游戏循环）
   * - 游戏状态非 Playing 但存在活跃粒子（播放死亡动画等）
   *
   * 注意：GameCanvas 组件内部也有自己的渲染循环。
   * 此处的循环负责游戏逻辑更新（tick），GameCanvas 负责渲染。
   * 两者通过 handleTick 回调桥接。
   */
  useEffect(() => {
    const shouldAnimate =
      gameState.status === GameStatus.Playing ||
      gameState.particles.length > 0;

    if (!shouldAnimate) {
      return;
    }

    // 重置时间基准
    lastTimeRef.current = 0;

    const gameLoop = (timestamp: number): void => {
      if (lastTimeRef.current === 0) {
        lastTimeRef.current = timestamp;
      }

      const deltaTime = Math.min(timestamp - lastTimeRef.current, 100);
      lastTimeRef.current = timestamp;

      handleTick(deltaTime);

      animationFrameRef.current = requestAnimationFrame(gameLoop);
    };

    animationFrameRef.current = requestAnimationFrame(gameLoop);

    return () => {
      if (animationFrameRef.current !== 0) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = 0;
      }
      lastTimeRef.current = 0;
    };
  }, [gameState.status, gameState.particles.length, handleTick]);

  // ==========================================================================
  // 动态渐变色计算
  // ==========================================================================

  /**
   * 根据当前得分获取蛇身渐变色，用于动态设置游戏画布容器边框颜色。
   */
  const snakeColors = getSnakeGradientColors(gameState.score.current);

  // ==========================================================================
  // 渲染
  // ==========================================================================

  return (
    <div className="app">
      {/* ================================================================ */}
      {/* 顶部信息栏 */}
      {/* ================================================================ */}
            <header className="app-header">
        <h1 className="app-title">霓虹蛇境</h1>
        <ScoreBoard score={gameState.score} />
        <div className="lives-display">
          {'❤️'.repeat(Math.max(0, gameState.lives))}
          <span className="lives-text">生命: {gameState.lives}</span>
        </div>
        <ActiveEffects effects={gameState.activeEffects} />
      </header>

      {/* ================================================================ */}
      {/* 游戏画布区域 */}
      {/* ================================================================ */}
      <main className="game-area">
        <div
          className="game-canvas-container"
          style={{
            borderColor: snakeColors[0] || COLOR_SCHEME.snakeGradientEarly[0],
          }}
        >
          <GameCanvas
            gameState={gameState}
            onTick={handleTick}
          />
        </div>
      </main>

      {/* ================================================================ */}
      {/* 底部操作提示 */}
      {/* ================================================================ */}
      <footer className="app-footer">
        <p className="control-hints">
          {isMobile ? (
            <span>使用屏幕按钮控制方向</span>
          ) : (
            <span>
              <kbd>↑↓←→</kbd> 或 <kbd>W A S D</kbd> 控制方向
            </span>
          )}
        </p>
      </footer>

      {/* ================================================================ */}
      {/* 游戏覆盖层（开始提示 / 结束弹窗） */}
      {/* ================================================================ */}
      <GameOverlay
        status={gameState.status}
        score={gameState.score}
        onStart={() => dispatch({ type: 'START_GAME' })}
      />
    </div>
  );
};

export default App;